// ConversationSidePanelContent.js
import React, {
    useState,
    useEffect,
    useCallback,
    useRef,
    useContext,
    Suspense,
    useMemo,
} from "react";
import axios from "axios";
import ScenarioManager from "../../components/ScenarioManager";
import InviteFriendDialog from "../../components/InviteFriendDialog";
import WebSocketService from "../../context/WebSocketService";
import { GlobalContext } from "../../context/GlobalContext";
import "./ConversationSidePanelContent.css";

const ENABLE_ADMIN_TEST_TOOLS = false;
const ConversationSidePanelTestingTools = React.lazy(() =>
    import("./ConversationSidePanelTestingTools")
);

const INITIAL_VISIBLE_COUNT = 5;
const LOAD_MORE_INCREMENT = 5;

const LoadMoreListItem = ({ onClick, label = "Load more" }) => {
    if (!onClick) {
        return null;
    }

    return (
        <li className="load-more-item">
            <button type="button" className="load-more-button" onClick={onClick}>
                {label}
            </button>
        </li>
    );
};

const ConversationSidePanelContent = ({
    conversationDetails,
    setConversationDetails,
    scenarioState,
    setScenarioState,
    handleResetConversation,
    isPanelOpen,
    setIsPanelOpen,
}) => {
    const { viewerMode, auth, preferences } = useContext(GlobalContext);
    const showNotificationTestSection =
        ENABLE_ADMIN_TEST_TOOLS && auth?.userType === "admin";
    const [friendConversations, setFriendConversations] = useState([]);
    const [scenarioConversations, setScenarioConversations] = useState([]);
    const [chatConversations, setChatConversations] = useState([]);
    const [promptConversations, setPromptConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [visibleCounts, setVisibleCounts] = useState({
        friends: INITIAL_VISIBLE_COUNT,
        prompts: INITIAL_VISIBLE_COUNT,
        scenarios: INITIAL_VISIBLE_COUNT,
        chats: INITIAL_VISIBLE_COUNT,
    });
    const scenarioManagerRef = useRef(null);

    const userTargetLanguage = preferences?.language;

    const shouldShowUpdateLanguage = useCallback(
        (conversationTargetLanguage) => {
            if (!userTargetLanguage || !conversationTargetLanguage) {
                return false;
            }
            return conversationTargetLanguage !== userTargetLanguage;
        },
        [userTargetLanguage]
    );

    const adjustVisibleCount = useCallback((prevCount, listLength) => {
        if (listLength === 0) {
            return INITIAL_VISIBLE_COUNT;
        }
        return Math.min(Math.max(INITIAL_VISIBLE_COUNT, prevCount), listLength);
    }, []);

    const categorizeConversations = useCallback(
        (allConversations) => {
            const friends = allConversations.filter(
                (conv) =>
                    conv.conversationType !== "AI" &&
                    conv.conversationType !== "none" &&
                    conv.conversationType !== "prompt"
            );
            const scenarios = allConversations.filter(
                (conv) =>
                    conv.conversationType === "AI" && conv.persona === "Roleplay Partner"
            );
            const chats = allConversations.filter(
                (conv) =>
                    (conv.conversationType === "AI" &&
                        conv.persona !== "Roleplay Partner") ||
                    conv.conversationType === "none"
            );
            const prompts = allConversations.filter(
                (conv) => conv.conversationType === "prompt"
            );

            setFriendConversations(friends);
            setScenarioConversations(scenarios);
            setChatConversations(chats);
            setPromptConversations(prompts);
            setVisibleCounts((prev) => ({
                friends: adjustVisibleCount(prev.friends, friends.length),
                prompts: adjustVisibleCount(prev.prompts, prompts.length),
                scenarios: adjustVisibleCount(prev.scenarios, scenarios.length),
                chats: adjustVisibleCount(prev.chats, chats.length),
            }));
        },
        [adjustVisibleCount]
    );

    const fetchConversations = useCallback(async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/conversation/titles` +
                    (viewerMode ? "?viewer=1" : "")
            );
            categorizeConversations(response.data);
        } catch (error) {
            console.error("Error fetching conversation titles:", error);
        }
    }, [categorizeConversations, viewerMode]);

    const dialogRefInvite = useRef(null);
    const openInviteDialog = () => {
        dialogRefInvite.current.open = true;
    };

    const openScenarioManager = () => {
        scenarioManagerRef.current.openScenarioManagerDefault();
    };

    const createAssistantConversation = async () => {
        await axios.post(`${process.env.REACT_APP_API_URL}/petpal/create-petpal`, {
            withCredentials: true,
        });
        fetchConversations();
        // Optionally close the panel if needed
    };

    const [creatingPrompt, setCreatingPrompt] = useState(false);

    const createPromptConversationNow = async () => {
        if (creatingPrompt) return;
        setCreatingPrompt(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/prompt/create-now`,
                {},
                { withCredentials: true }
            );
            fetchConversations();
        } catch (error) {
            console.error("Error creating prompt conversation:", error);
        } finally {
            setCreatingPrompt(false);
        }
    };

    const fetchConversationById = useCallback(
        async (conversation) => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/conversation/by-id/${conversation._id}` +
                        (viewerMode ? "?viewer=1" : "")
                );
                // Rename _id to conversationId and sentences to conversationHistory,
                // while extracting scenario separately and gathering remaining fields.
                const {
                    _id: conversationId,
                    conversationHistory,
                    scenario,
                    ...otherDetails
                } = response.data;
                // setConversationDetails without the scenario field
                setConversationDetails({
                    conversationId,
                    conversationHistory,
                    ...otherDetails,
                });
                // Store scenario separately
                setScenarioState(scenario);
                setSelectedConversation(conversation);
            } catch (error) {
                console.error("Error fetching conversation:", error);
            }
        },
        [setConversationDetails, setScenarioState, viewerMode]
    );

    const handleSelectConversation = useCallback(
        async (conversation) => {
            // 1. Load the conversation exactly as before
            await fetchConversationById(conversation);

            // 2. Check if the panel overlaps the conversation area
            const panelEl = document.querySelector(".side-panel.open");
            const convoEl = document.querySelector(".conversation-area");
            if (panelEl && convoEl) {
                const panelRect = panelEl.getBoundingClientRect();
                const convoRect = convoEl.getBoundingClientRect();
                const isCovering = panelRect.right > convoRect.left; // simple overlap test
                if (isCovering) setIsPanelOpen(false); // close only when needed
            }
        },
        [fetchConversationById, setIsPanelOpen]
    );

    const handleDeleteConversation = async (conversationId) => {
        try {
            if (conversationId === conversationDetails.conversationId) {
                handleResetConversation();
            }
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/conversation/delete/${conversationId}`
            );
            fetchConversations();
        } catch (error) {
            console.error("Error deleting conversation:", error);
        }
    };
    // Add this function within your ConversationSidePanelContent component
    const updateConversationLanguage = async (conversationId) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/conversation/update-language`,
                { conversationId },
                { withCredentials: true }
            );
            // Optionally update local state with the new targetLanguage
            setConversationDetails((prev) => ({
                ...prev,
                targetLanguage: response.data.targetLanguage,
            }));
            // console.log(response.data.message);
        } catch (error) {
            console.error("Error updating conversation language:", error);
        }
    };

    const handleLoadMore = useCallback((category, total) => {
        setVisibleCounts((prev) => ({
            ...prev,
            [category]: Math.min(prev[category] + LOAD_MORE_INCREMENT, total),
        }));
    }, []);

    useEffect(() => {
        const handleMessage = (data) => {
            if (data.conversations) {
                categorizeConversations(data.conversations);
            }
        };
        WebSocketService.on("message", handleMessage);
        return () => {
            WebSocketService.off("message", handleMessage);
        };
    }, [categorizeConversations]);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    useEffect(() => {
        if (isPanelOpen) fetchConversations();
    }, [fetchConversations, isPanelOpen]);

    useEffect(() => {
        const allConversations = [
            ...friendConversations,
            ...chatConversations,
            ...scenarioConversations,
            ...promptConversations,
        ];
        const setupMenuToggle = (menuSelector, anchorSelector) => {
            const toggleMenu = () => {
                const menuEl = document.body.querySelector(menuSelector);
                if (menuEl) {
                    menuEl.open = !menuEl.open;
                }
            };
            const anchorEl = document.body.querySelector(anchorSelector);
            if (anchorEl) {
                anchorEl.addEventListener("click", toggleMenu);
            }
            return () => {
                if (anchorEl) {
                    anchorEl.removeEventListener("click", toggleMenu);
                }
            };
        };

        const cleanupFunctions = allConversations.map((conversation) => {
            const buttonId = `conversation-actions-menu-anchor-${conversation._id}`;
            const menuId = `conversation-actions-menu-${conversation._id}`;
            return setupMenuToggle(`#${menuId}`, `#${buttonId}`);
        });

        return () => {
            cleanupFunctions.forEach((cleanup) => cleanup());
        };
    }, [
        friendConversations,
        chatConversations,
        scenarioConversations,
        promptConversations,
        visibleCounts,
    ]);

    const allConversations = useMemo(
        () => [
            ...friendConversations,
            ...chatConversations,
            ...scenarioConversations,
            ...promptConversations,
        ],
        [
            friendConversations,
            chatConversations,
            scenarioConversations,
            promptConversations,
        ]
    );

    useEffect(() => {
        const activeConversationId = conversationDetails?.conversationId;
        if (!activeConversationId) {
            setSelectedConversation(null);
            return;
        }

        const matchedConversation = allConversations.find(
            (conversation) => conversation._id === activeConversationId
        );

        setSelectedConversation((prevSelected) => {
            if (matchedConversation) {
                if (
                    prevSelected?._id === matchedConversation._id &&
                    prevSelected?.title === matchedConversation.title
                ) {
                    return prevSelected;
                }
                return matchedConversation;
            }

            if (prevSelected?._id === activeConversationId && !prevSelected?.title) {
                return prevSelected;
            }

            return { _id: activeConversationId };
        });
    }, [allConversations, conversationDetails?.conversationId]);

    const visibleFriendConversations = friendConversations.slice(
        0,
        visibleCounts.friends
    );
    const visiblePromptConversations = promptConversations.slice(
        0,
        visibleCounts.prompts
    );
    const visibleScenarioConversations = scenarioConversations.slice(
        0,
        visibleCounts.scenarios
    );
    const visibleChatConversations = chatConversations.slice(0, visibleCounts.chats);

    const hasMoreFriends = friendConversations.length > visibleCounts.friends;
    const hasMorePrompts = promptConversations.length > visibleCounts.prompts;
    const hasMoreScenarios = scenarioConversations.length > visibleCounts.scenarios;
    const hasMoreChats = chatConversations.length > visibleCounts.chats;

    return (
        <div className="side-panel-content">
            {showNotificationTestSection && (
                <Suspense fallback={null}>
                    <ConversationSidePanelTestingTools
                        fetchConversations={fetchConversations}
                    />
                </Suspense>
            )}
            {/* Friends Section */}
            <h3 className="h3-select-conversation">
                <span>Friends</span>
                <md-icon-button onClick={openInviteDialog}>
                    <md-icon>person_add</md-icon>
                </md-icon-button>
            </h3>
            <ul>
                <li onClick={openInviteDialog}>
                    Invite a Friend
                    <md-icon>add</md-icon>
                </li>
                {visibleFriendConversations.map((conversation) => {
                    const buttonId = `conversation-actions-menu-anchor-${conversation._id}`;
                    const menuId = `conversation-actions-menu-${conversation._id}`;
                    return (
                        <li
                            key={conversation._id}
                            className={
                                conversation._id === selectedConversation?._id
                                    ? "selected"
                                    : ""
                            }
                            onClick={async () => {
                                await handleSelectConversation(conversation);
                            }}
                        >
                            {conversation.title}
                            {!viewerMode && (
                                <span className="inline-flex-container">
                                    <md-icon-button
                                        id={buttonId}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <md-icon>more_horiz</md-icon>
                                    </md-icon-button>
                                    <md-menu
                                        anchor-corner="start-end"
                                        menu-corner="start-end"
                                        id={menuId}
                                        anchor={buttonId}
                                    >
                                        <md-menu-item
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conversation._id);
                                            }}
                                        >
                                            <span
                                                style={{ color: "var(--md-sys-color-error)" }}
                                            >
                                                Delete
                                            </span>
                                            <md-icon
                                                style={{ color: "var(--md-sys-color-error)" }}
                                                slot="end"
                                            >
                                                delete
                                            </md-icon>
                                        </md-menu-item>
                                        {shouldShowUpdateLanguage(
                                            conversation.targetLanguage
                                        ) && (
                                            <md-menu-item
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateConversationLanguage(
                                                        conversation._id
                                                    );
                                                }}
                                            >
                                                <span>Update Language</span>
                                                <md-icon slot="end">language</md-icon>
                                            </md-menu-item>
                                        )}
                                    </md-menu>
                                </span>
                            )}
                        </li>
                    );
                })}
                {hasMoreFriends && (
                    <LoadMoreListItem
                        onClick={() =>
                            handleLoadMore("friends", friendConversations.length)
                        }
                    />
                )}
            </ul>
            <InviteFriendDialog dialogRefInvite={dialogRefInvite} />

            <h3 className="h3-select-conversation">
                <span>Conversation Starters</span>
                <md-icon-button
                    onClick={createPromptConversationNow}
                    {...(creatingPrompt ? { "soft-disabled": true } : {})}
                >
                    <md-icon>add</md-icon>
                </md-icon-button>
            </h3>
            <ul>
                {visiblePromptConversations.map((conversation) => {
                    const buttonId = `conversation-actions-menu-anchor-${conversation._id}`;
                    const menuId = `conversation-actions-menu-${conversation._id}`;
                    return (
                        <li
                            key={conversation._id}
                            className={
                                conversation._id === selectedConversation?._id
                                    ? "selected"
                                    : ""
                            }
                            onClick={async () => {
                                await handleSelectConversation(conversation);
                            }}
                        >
                            {conversation.title}
                            {!viewerMode && (
                                <span className="inline-flex-container">
                                    <md-icon-button
                                        id={buttonId}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <md-icon>more_horiz</md-icon>
                                    </md-icon-button>
                                    <md-menu
                                        anchor-corner="start-end"
                                        menu-corner="start-end"
                                        id={menuId}
                                        anchor={buttonId}
                                    >
                                        <md-menu-item
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conversation._id);
                                            }}
                                        >
                                            <span
                                                style={{ color: "var(--md-sys-color-error)" }}
                                            >
                                                Delete
                                            </span>
                                            <md-icon
                                                style={{ color: "var(--md-sys-color-error)" }}
                                                slot="end"
                                            >
                                                delete
                                            </md-icon>
                                        </md-menu-item>
                                        {shouldShowUpdateLanguage(
                                            conversation.targetLanguage
                                        ) && (
                                            <md-menu-item
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateConversationLanguage(
                                                        conversation._id
                                                    );
                                                }}
                                            >
                                                <span>Update Language</span>
                                                <md-icon slot="end">
                                                    language
                                                </md-icon>
                                            </md-menu-item>
                                        )}
                                    </md-menu>
                                </span>
                            )}
                        </li>
                    );
                })}
                {hasMorePrompts && (
                    <LoadMoreListItem
                        onClick={() =>
                            handleLoadMore("prompts", promptConversations.length)
                        }
                    />
                )}
            </ul>

            {/* Scenario Conversations Section */}
            <h3 className="h3-select-conversation">
                <span>Scenarios</span>{" "}
                <md-icon-button onClick={openScenarioManager}>
                    <md-icon>add</md-icon>
                </md-icon-button>
            </h3>
            <ul>
                <li onClick={openScenarioManager}>
                    New Scenario
                    <md-icon>add</md-icon>
                </li>
                {visibleScenarioConversations.map((conversation) => {
                    const buttonId = `conversation-actions-menu-anchor-${conversation._id}`;
                    const menuId = `conversation-actions-menu-${conversation._id}`;
                    return (
                        <li
                            key={conversation._id}
                            className={
                                conversation._id === selectedConversation?._id
                                    ? "selected"
                                    : ""
                            }
                            onClick={async () => {
                                await handleSelectConversation(conversation);
                            }}
                        >
                            {conversation.title}
                            {!viewerMode && (
                                <span className="inline-flex-container">
                                    <md-icon-button
                                        id={buttonId}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <md-icon>more_horiz</md-icon>
                                    </md-icon-button>
                                    <md-menu
                                        anchor-corner="start-end"
                                        menu-corner="start-end"
                                        id={menuId}
                                        anchor={buttonId}
                                    >
                                        <md-menu-item
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conversation._id);
                                            }}
                                        >
                                            <span
                                                style={{ color: "var(--md-sys-color-error)" }}
                                            >
                                                Delete
                                            </span>
                                            <md-icon
                                                style={{ color: "var(--md-sys-color-error)" }}
                                                slot="end"
                                            >
                                                delete
                                            </md-icon>
                                        </md-menu-item>
                                        {shouldShowUpdateLanguage(
                                            conversation.targetLanguage
                                        ) && (
                                            <md-menu-item
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateConversationLanguage(
                                                        conversation._id
                                                    );
                                                }}
                                            >
                                                <span>Update Language</span>
                                                <md-icon slot="end">language</md-icon>
                                            </md-menu-item>
                                        )}
                                    </md-menu>
                                </span>
                            )}
                        </li>
                    );
                })}
                {hasMoreScenarios && (
                    <LoadMoreListItem
                        onClick={() =>
                            handleLoadMore("scenarios", scenarioConversations.length)
                        }
                    />
                )}
            </ul>

            {/* Chat Conversations Section */}
            <h3 className="h3-select-conversation">
                <span>Chats</span>{" "}
                <md-icon-button onClick={handleResetConversation}>
                    <md-icon>edit_square</md-icon>
                </md-icon-button>
            </h3>
            <ul>
                <li onClick={createAssistantConversation}>
                    Restart Tutorial
                    <md-icon>Help</md-icon>
                </li>
                {visibleChatConversations.map((conversation) => {
                    const buttonId = `conversation-actions-menu-anchor-${conversation._id}`;
                    const menuId = `conversation-actions-menu-${conversation._id}`;
                    return (
                        <li
                            key={conversation._id}
                            className={
                                conversation._id === selectedConversation?._id
                                    ? "selected"
                                    : ""
                            }
                            onClick={async () => {
                                await fetchConversationById(conversation);
                            }}
                        >
                            {conversation.title}
                            {!viewerMode && (
                                <span className="inline-flex-container">
                                    <md-icon-button
                                        id={buttonId}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <md-icon>more_horiz</md-icon>
                                    </md-icon-button>
                                    <md-menu
                                        anchor-corner="start-end"
                                        menu-corner="start-end"
                                        id={menuId}
                                        anchor={buttonId}
                                    >
                                        <md-menu-item
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteConversation(conversation._id);
                                            }}
                                        >
                                            <span
                                                style={{ color: "var(--md-sys-color-error)" }}
                                            >
                                                Delete
                                            </span>
                                            <md-icon
                                                style={{ color: "var(--md-sys-color-error)" }}
                                                slot="end"
                                            >
                                                delete
                                            </md-icon>
                                        </md-menu-item>
                                        {shouldShowUpdateLanguage(
                                            conversation.targetLanguage
                                        ) && (
                                            <md-menu-item
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    updateConversationLanguage(
                                                        conversation._id
                                                    );
                                                }}
                                            >
                                                <span>Update Language</span>
                                                <md-icon slot="end">language</md-icon>
                                            </md-menu-item>
                                        )}
                                    </md-menu>
                                </span>
                            )}
                        </li>
                    );
                })}
                {hasMoreChats && (
                    <LoadMoreListItem
                        onClick={() =>
                            handleLoadMore("chats", chatConversations.length)
                        }
                    />
                )}
            </ul>

            <ScenarioManager
                ref={scenarioManagerRef}
                scenarioState={scenarioState}
                setScenarioState={setScenarioState}
                setIsPanelOpen={setIsPanelOpen}
            />
        </div>
    );
};

export default ConversationSidePanelContent;
